import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import { MessageCircle, ArrowDown, Download } from "lucide-react";

const WHATSAPP_URL = "https://wa.me/34672808030";

const HeroSection = () => {
  const { lang } = useLang();
  const t = translations[lang].hero;
  const dossierText = translations[lang].dossier.cta;

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-navy">
      <div className="absolute inset-0 hero-grid opacity-60" />
      <div className="hero-orb hero-orb-1" />
      <div className="hero-orb hero-orb-2" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 pt-36 pb-20 md:pt-40 md:pb-28 w-full">
        {/* Mobile video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="lg:hidden mb-8"
        >
          <div className="rounded-2xl overflow-hidden shadow-xl border border-white/10">
            <video
              src="/hero-video.mp4"
              data-testid="hero-video-mobile"
              className="w-full h-[200px] sm:h-[260px] object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster="/port-aerial.jpg"
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-electric-light mb-6 font-body"
            >
              {t.overline}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-[1.05] tracking-tight font-heading mb-8"
            >
              {t.headline}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-xl mb-10 font-body"
            >
              {t.subheadline}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="flex flex-col sm:flex-row gap-4 flex-wrap"
            >
              <a
                data-testid="hero-cta-whatsapp"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 bg-electric text-white font-semibold py-4 px-8 rounded-full hover:bg-electric-hover transition-all duration-300 shadow-lg shadow-electric/30 hover:shadow-electric/50 text-base md:text-lg"
              >
                <MessageCircle size={20} />
                {t.cta}
              </a>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-medium py-4 px-8 rounded-full hover:bg-white/20 transition-all duration-300 border border-white/15 backdrop-blur-sm text-base md:text-lg"
              >
                {t.ctaSecondary}
                <ArrowDown size={18} />
              </button>
              <a
                data-testid="hero-cta-dossier"
                href="/catalogo-okontainers.pdf"
                download
                className="inline-flex items-center justify-center gap-2 text-slate-300 hover:text-white font-medium py-4 px-6 rounded-full hover:bg-white/5 transition-all duration-300 text-sm md:text-base"
              >
                <Download size={16} />
                {dossierText}
              </a>
            </motion.div>
          </div>

          {/* Desktop video */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-electric/10 rounded-3xl blur-2xl" />
              <div
                data-testid="hero-video-desktop"
                className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-white/10"
              >
                <video
                  src="/hero-video.mp4"
                  className="w-full h-[420px] object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  poster="/port-aerial.jpg"
                />
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-5 h-8 rounded-full border-2 border-white/30 flex items-start justify-center pt-1"
          >
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
